import { throttle } from 'lodash';
import { useEffect, useRef } from 'react';

import simpleheat from 'simpleheat';

export const useHeatmap = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const heatmap = useRef<ReturnType<typeof simpleheat>>(undefined);

  const compressionRatio = 4;

  const drawHeatmap = throttle(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const { scrollWidth, scrollHeight } = document.body;

    const width = scrollWidth / compressionRatio;
    const height = scrollHeight / compressionRatio;

    if (canvas.width != width || canvas.height != height) {
      canvas.width = width;
      canvas.height = height;

      canvas.style.height = canvas.height * compressionRatio + 'px';
      canvas.style.width = canvas.width * compressionRatio + 'px';

      heatmap.current?.resize();
    }

    heatmap.current?.draw();
  }, 10000);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    heatmap.current = simpleheat(canvas)
      .max(18)
      .radius(40 / compressionRatio, 25 / compressionRatio);

    drawHeatmap();

    const mouseMoveHandler = (event: MouseEvent) => {
      heatmap.current?.add([
        event.pageX / compressionRatio,
        event.pageY / compressionRatio,
        1,
      ]);

      requestAnimationFrame(drawHeatmap);
    };
    const touchMoveHandler = (event: TouchEvent) => {
      Array.from(event.touches).forEach((touch) => {
        for (let i = 0; i < 3; i++) {
          heatmap.current?.add([
            touch.pageX / compressionRatio,
            touch.pageY / compressionRatio,
            1,
          ]);
        }
      });

      requestAnimationFrame(drawHeatmap);
    };

    document.body.addEventListener('mousemove', mouseMoveHandler);
    document.body.addEventListener('touchmove', touchMoveHandler);
    document.body.addEventListener('touchstart', touchMoveHandler);

    return () => {
      document.body.removeEventListener('mousemove', mouseMoveHandler);
      document.body.removeEventListener('touchmove', touchMoveHandler);
      document.body.removeEventListener('touchstart', touchMoveHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- set up once on mount; drawHeatmap draws from refs
  }, []);

  const onResizeHandler = () => {
    requestAnimationFrame(drawHeatmap);
  };

  useEffect(() => {
    addEventListener('resize', onResizeHandler);

    return () => {
      removeEventListener('resize', onResizeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- subscribes once on mount; the handler draws from refs
  }, []);

  return { canvasRef };
};
